import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import {
    isSupportedLanguage,
    readLanguageChoice,
    writeLanguageChoice,
} from './models/language';

/**
 * The one statement of which language wins, and the only place `changeLanguage` is called
 * outside the header's own picker.
 *
 * ## The order, and why each rung is where it is
 *
 * 1. **The signed-in person's stored preference.** Theirs, kept server-side, so it follows
 *    them to a borrowed laptop. Applying it also *marks* this browser as having chosen --
 *    a stored preference is an explicit choice, just one made somewhere else.
 * 2. **A choice already made in this browser.** Never overruled by anything below. This is
 *    the rung that stops a clinic's default flipping a signed-out visitor who has already
 *    said what they want.
 * 3. **The clinic whose address this is.** Applied without marking a choice, because nobody
 *    made one -- so a visitor who later picks a language still overrides it, and a clinic
 *    that changes its default still reaches them.
 * 4. **Nothing.** i18next's detector already settled on something from the browser; leave it.
 *
 * ## Why it is one hook rather than two effects
 *
 * The rule spans `AuthContext` and `BrandingContext`, and half a rule in each is how the two
 * halves drift. It reads both and writes neither.
 *
 * `AuthContext` in particular stays untouched: its own comment warns that giving it a
 * `useTranslation` would change what forty test files render. Nothing needs it to -- `login()`
 * already persists the whole user to localStorage, so `language` rides along and the sign-in
 * and rehydrate paths both end in the same state this hook reads.
 *
 * Mounted once, near the top of the tree, by `<LanguagePreference />`. Deliberately **not**
 * inside `test-utils`' providers: the component tests that stub a signed-in user would start
 * changing language underneath themselves.
 */
export const useLanguagePreference = (): void => {
    const { user, isAuthenticated } = useAuth();
    const { hostContext } = useBranding();
    const { i18n } = useTranslation();

    const stored = user?.language;
    const clinicDefault = hostContext?.defaultLanguage;

    useEffect(() => {
        // Compared against `resolvedLanguage` rather than `language`: the latter can carry a
        // region ("ro-RO") that will never equal a stored "ro", so the guard would never hold
        // and the effect would re-fire on the render its own change caused.
        const current = i18n.resolvedLanguage ?? i18n.language;

        if (isAuthenticated && isSupportedLanguage(stored)) {
            if (stored !== current) {
                void i18n.changeLanguage(stored);
            }

            writeLanguageChoice(stored);

            return;
        }

        if (readLanguageChoice()) {
            return;
        }

        if (isSupportedLanguage(clinicDefault) && clinicDefault !== current) {
            void i18n.changeLanguage(clinicDefault);
        }
    }, [isAuthenticated, stored, clinicDefault, i18n]);
};

/**
 * The hook, as something that can be mounted. Renders nothing.
 *
 * A component rather than a call inside `App`, so the effect sits at a fixed point in the
 * tree instead of re-running whenever `App` re-renders for an unrelated reason.
 */
export const LanguagePreference = (): null => {
    useLanguagePreference();

    return null;
};
